import React from 'react'
import { fetchNudgeQueueDetail } from '../../../lib/nudgeApi'
import { NudgeStatusBadge } from './NudgeStatusBadge'
import { ChatTimeline } from './ChatTimeline'
import { formatDateTime } from '../utils'
import { useDashboard } from '../DashboardContext'
import { Skeleton } from '../../../components/ui/Skeleton'
import type { NudgeQueueSummary, NudgeQueueDetail } from '../../../types/api'

export function ConversationThread({
  summaries,
  onClose,
}: {
  summaries: NudgeQueueSummary[]
  onClose: () => void
}) {
  const { businessInfo, isFeatureAllowed } = useDashboard()
  const [details, setDetails] = React.useState<NudgeQueueDetail[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [infoOpen, setInfoOpen] = React.useState(false)

  const nudgeIds = summaries.map(s => s.id).join(',')

  React.useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    setDetails([])

    Promise.all(summaries.map(s => fetchNudgeQueueDetail(s.id)))
      .then(results => { if (!cancelled) setDetails(results) })
      .catch(() => { if (!cancelled) setError('Failed to load conversation details.') })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudgeIds])

  const latest = summaries[summaries.length - 1]
  const businessName = businessInfo?.businessName?.trim() || 'You'
  const customerName = latest.contactName?.trim() || 'Customer'
  const aiGated =
    !isFeatureAllowed('NUDGE_REPLY_AI') &&
    summaries.some(n => n.status === 'REPLY_RECEIVED')

  return (
    <>
      <div className="conv-backdrop" onClick={onClose} />
      <div className="conv-panel">
        <div className="conv-header">
          <div className="conv-header-meta">
            <div className="conv-contact-name">{latest.contactName}</div>
            <div className="conv-contact-email">{latest.contactEmail}</div>
            <div className="conv-badges">
              <NudgeStatusBadge status={latest.status} />
              {summaries.length === 1 ? (
                <span className="pill pill-pending">Nudge #{latest.sequenceNumber}</span>
              ) : (
                <span className="pill pill-pending">{summaries.length} nudges</span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={() => setInfoOpen(o => !o)}
              disabled={loading}
              aria-label="Thread info"
              aria-expanded={infoOpen}
              style={{
                width: 30,
                height: 30,
                border: `1px solid ${infoOpen ? 'var(--accent)' : 'var(--border)'}`,
                borderRadius: 6,
                background: infoOpen ? 'var(--accent-bg)' : 'transparent',
                color: infoOpen ? 'var(--accent)' : 'var(--text2)',
                cursor: loading ? 'default' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: loading ? 0.4 : 1,
                padding: 0,
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>

        {infoOpen && details.length > 0 ? (
          <div
            style={{
              display: 'grid',
              gap: 6,
              padding: '12px 20px',
              background: 'var(--surface)',
              borderBottom: '1px solid var(--border)',
              flexShrink: 0,
            }}
          >
            {details.map(d => (
              <React.Fragment key={d.id}>
                {details.length > 1 ? (
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: 'var(--text3)',
                      fontFamily: "'DM Mono',monospace",
                      letterSpacing: '0.06em',
                      marginTop: 4,
                    }}
                  >
                    NUDGE #{d.sequenceNumber}
                  </div>
                ) : null}
                <TimestampRow label="Created" value={d.createdAt} />
                {d.sentAt ? <TimestampRow label="Sent" value={d.sentAt} /> : null}
                {d.followUpSendAfter ? (
                  <TimestampRow label="Next follow-up" value={d.followUpSendAfter} />
                ) : null}
                {d.replyReceivedAt ? (
                  <TimestampRow label="Reply received" value={d.replyReceivedAt} />
                ) : null}
                {d.recoveredAt ? (
                  <TimestampRow label="Recovered at" value={d.recoveredAt} />
                ) : null}
              </React.Fragment>
            ))}
          </div>
        ) : null}

        <div className="conv-body" style={{ padding: 0, display: 'flex', flexDirection: 'column' }}>
          {loading ? (
            <div
              aria-busy="true"
              aria-label="Loading conversation"
              style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}
            >
              <Skeleton height={80} borderRadius={10} />
              <Skeleton height={40} borderRadius={8} />
              <Skeleton height={60} borderRadius={10} />
              <Skeleton height={40} borderRadius={8} />
            </div>
          ) : error ? (
            <div style={{ padding: '20px 24px' }}>
              <div className="conv-alert failed">{error}</div>
            </div>
          ) : details.length > 0 ? (
            <ChatTimeline
              details={details}
              businessName={businessName}
              customerName={customerName}
              aiGated={aiGated}
            />
          ) : null}
        </div>
      </div>
    </>
  )
}

function TimestampRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '6px 10px',
        background: 'var(--surface2)',
        borderRadius: 6,
        border: '1px solid var(--border)',
      }}
    >
      <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'DM Mono',monospace" }}>
        {label}
      </span>
      <span style={{ fontSize: 11, color: 'var(--text2)', fontFamily: "'DM Mono',monospace" }}>
        {formatDateTime(value)}
      </span>
    </div>
  )
}
